import React, { createContext, useContext, useEffect, useState } from 'react';
import io from 'socket.io-client';
import { useAuth } from './AuthContext';
import { SOCKET_BASE_URL } from '../config/api';

const SocketContext = createContext();

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

export const SocketProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const [onlineUsers, setOnlineUsers] = useState(new Set());
  const [notifications, setNotifications] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const { user } = useAuth();

  const resolvedUserId = user?._id || user?.id;

  useEffect(() => {
    console.log('🔌 SocketContext - useEffect triggered');
    console.log('🔌 SocketContext - user data:', user);
    console.log('🔌 SocketContext - resolved user id:', resolvedUserId);
    console.log('🔌 SocketContext - user object changed:', JSON.stringify(user, null, 2));

    if (!resolvedUserId) {
      console.log('🔌 Waiting for user data before connecting socket...');
      if (socket) {
        socket.disconnect();
        setSocket(null);
      }
      setIsConnected(false);
      setOnlineUsers(new Set());
      return;
    }

    // Prevent multiple socket instances
    if (socket && socket.connected) {
      console.log('🔌 Socket already connected, skipping...');
      return;
    }

    const socketInstance = io(SOCKET_BASE_URL, {
      withCredentials: true,
      transports: ['websocket'], // Only use websocket for now
      autoConnect: true,
      reconnection: false, // Disable reconnection temporarily
      timeout: 10000
    });

    console.log('🔌 Initializing socket connection for user:', resolvedUserId);

    // Connection established
    socketInstance.on('connect', () => {
      console.log('✅ Connected to Socket.io server');
      setIsConnected(true);
      socketInstance.emit('join_user_room', resolvedUserId);
    });

    // Handle reconnection attempts
    socketInstance.on('reconnect_attempt', (attempt) => {
      console.log(`🔄 Reconnection attempt ${attempt}/5`);
    });

    // Handle reconnection failure
    socketInstance.on('reconnect_failed', () => {
      console.error('❌ Failed to reconnect to Socket.io server');
      setIsConnected(false);
    });

    // Listen for incoming messages
    socketInstance.on('receive_message', (message) => {
      console.log('📨 Received message:', message);
      // The Messages component will handle this through context or props
      // We emit a custom event that Messages can listen to
      window.dispatchEvent(new CustomEvent('socket_message', { detail: message }));
    });

    // Listen for new message notifications
    socketInstance.on('new_message_notification', (notification) => {
      console.log('🔔 New message notification:', notification);

      // Check if the notification is for the current user (prevent self-notification)
      if (notification.message.senderId === resolvedUserId) {
        console.log('🚫 Ignoring self-notification');
        return;
      }

      setNotifications(prev => [notification, ...prev].slice(0, 10)); // Keep only last 10 notifications

      // Show browser notification if supported
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification(`New message from ${notification.message.senderName}`, {
          body: notification.message.text,
          icon: '/favicon.ico'
        });
      }
    });

    // Listen for real-time notifications (likes, matches, etc.)
    socketInstance.on('real_time_notification', (notification) => {
      console.log('🔔 Real-time notification:', notification);

      // Add to notifications list
      setNotifications(prev => [{
        type: notification.type,
        message: {
          text: notification.message,
          senderName: notification.data.likerName || notification.data.matchedUserName || 'System',
          senderId: notification.data.likerId || notification.data.matchedUserId || 'system',
          timestamp: notification.timestamp
        },
        data: notification.data
      }, ...prev].slice(0, 10));

      // Show browser notification if supported
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification(notification.type === 'profile_like' ? 'New Profile Like!' : 'New Match!', {
          body: notification.message,
          icon: '/favicon.ico'
        });
      }
    });

    // Listen for typing indicators
    socketInstance.on('user_typing', ({ conversationId, userName, isTyping }) => {
      console.log(`${userName} is ${isTyping ? 'typing' : 'not typing'}`);
      window.dispatchEvent(
        new CustomEvent('socket_typing', {
          detail: { conversationId, userName, isTyping }
        })
      );
    });

    // Listen for conversation deletion
    socketInstance.on('conversation_deleted', (data) => {
      console.log('🗑️ Conversation deleted:', data);
      window.dispatchEvent(
        new CustomEvent('socket_conversation_deleted', {
          detail: data
        })
      );
    });

    // Listen for message request received (real-time notification for recipient)
    socketInstance.on('message_request_received', (data) => {
      console.log('📨 Message request received:', data);
      window.dispatchEvent(
        new CustomEvent('socket_message_request_received', { detail: data })
      );
    });

    // Listen for message request response (accepted/declined notification for sender)
    socketInstance.on('message_request_responded', (data) => {
      console.log('✅ Message request responded:', data);
      window.dispatchEvent(
        new CustomEvent('socket_message_request_responded', { detail: data })
      );
    });

    // Listen for user status changes
    socketInstance.on('user_status_changed', ({ userId, status }) => {
      console.log(`👤 User ${userId} is now ${status}`);
      const normalizedUserId = String(userId);
      if (status === 'online') {
        setOnlineUsers(prev => new Set(prev).add(normalizedUserId));
      } else {
        setOnlineUsers(prev => {
          const newSet = new Set(prev);
          newSet.delete(normalizedUserId);
          return newSet;
        });
      }
    });

    // Receive the initial list of currently-online users
    socketInstance.on('online_users', (userIds) => {
      try {
        const normalized = Array.isArray(userIds) ? userIds.map((id) => String(id)) : [];
        setOnlineUsers(new Set(normalized));
      } catch (e) {
        console.error('Failed to process online_users payload:', e);
      }
    });

    // Handle connection errors
    socketInstance.on('connect_error', (error) => {
      console.error('❌ Socket connection error:', error);
      setIsConnected(false);
    });

    // Handle disconnection
    socketInstance.on('disconnect', () => {
      console.log('🔌 Disconnected from Socket.io server');
      setIsConnected(false);
    });

    setSocket(socketInstance);

    // Cleanup function
    return () => {
      console.log('🔌 Cleaning up socket connection...');
      socketInstance.off('connect');
      socketInstance.off('disconnect');
      socketInstance.off('reconnect_attempt');
      socketInstance.off('reconnect_failed');
      socketInstance.off('receive_message');
      socketInstance.off('new_message_notification');
      socketInstance.off('real_time_notification');
      socketInstance.off('user_typing');
      socketInstance.off('conversation_deleted');
      socketInstance.off('message_request_received');
      socketInstance.off('message_request_responded');
      socketInstance.off('user_status_changed');
      socketInstance.off('online_users');
      socketInstance.off('connect_error');
      socketInstance.disconnect();
      setSocket(null);
      setOnlineUsers(new Set());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedUserId]);

  const joinConversation = (conversationId) => {
    if (socket) {
      socket.emit('join_conversation', conversationId);
    }
  };

  const leaveConversation = (conversationId) => {
    if (socket) {
      socket.emit('leave_conversation', conversationId);
    }
  };

  const sendMessage = (conversationId, message, senderId, receiverId) => {
    if (socket) {
      socket.emit('send_message', {
        conversationId,
        message,
        senderId,
        receiverId
      });
    }
  };

  const startTyping = (conversationId, userName) => {
    if (socket) {
      socket.emit('typing_start', { conversationId, userName });
    }
  };

  const stopTyping = (conversationId) => {
    if (socket) {
      socket.emit('typing_stop', { conversationId });
    }
  };

  const clearNotifications = () => {
    setNotifications([]);
  };

  const value = {
    socket,
    onlineUsers,
    notifications,
    joinConversation,
    leaveConversation,
    sendMessage,
    startTyping,
    stopTyping,
    updatePresence: (isVisible) => socket?.emit('update_presence', isVisible),
    clearNotifications,
    isConnected
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};
